<script setup lang="ts">
import CarrefourIcon from '#assets/svg/carrefour.svg?component';
import HomeIcon from '#assets/svg/home.svg?component';
import IndicatorWidgetBreakdown from '#components/indicators/IndicatorWidgetBreakdown.vue';
import IndicatorWidgetChartBars from '#components/indicators/IndicatorWidgetChartBars.vue';
import IndicatorWidgetChartLines from '#components/indicators/IndicatorWidgetChartLines.vue';
import IndicatorWidgetChartPie from '#components/indicators/IndicatorWidgetChartPie.vue';
import { useLanguage } from '#composables/useLanguage';
import { formatIndicatorTitle, formatIndicatorValue, getComparisonClass } from '#helpers';
import type { Indicator } from '@carrefour-gcs/shared';
import { C4Tooltip } from '@c4-links/vue3-links-components';
import { computed } from 'vue';
import { ContentLoader } from 'vue-content-loader';
import VueFeather from 'vue-feather';
import IndicatorWidgetChartHorizontalBars from './IndicatorWidgetChartHorizontalBars.vue';

// --- Since we pass the entire result of a `useQuery` to this component
// --- We need to explicitly tell vue to not assign non-prop attributes
// --- to the root element(s).
defineOptions({
  inheritAttrs: false
});

// --- Allow custom visualizations.
defineSlots<{
  default(props: Indicator): unknown;
}>();

const props = defineProps<{
  type?: 'breakdown' | 'pie' | 'bars' | 'horizontal-bars' | 'lines';
  name?: string;
  error?: Error | null;
  data?: Indicator;
  isError?: boolean;
  isFetching?: boolean;
  isInitialLoading?: boolean;
  notif?: string;
  isHomeIcon?: boolean;
}>();

// --- Override `data.name` with `name` if defined.
const name = computed(() => props.name ?? props.data?.name);

// --- Compute the size of the loader based on the expected visualization.
// --- This mitigates most of the layout shift once the data is loaded.
// --- Note that this is not perfect and should be improved. But it's a start.
const classes = computed(() => ({
  'min-h-[270px]':
    props.type === 'pie' || props.type === 'bars' || props.type === 'horizontal-bars' || props.type === 'lines',
  'min-h-[135px]': !props.type || props.type === 'breakdown'
}));

// --- Instantiate composables.
const { t, language } = useLanguage();
</script>

<template>
  <!-- Initial Loader -->
  <div v-if="isInitialLoading" class="relative overflow-hidden p-6 w-full" :class="classes">
    <ContentLoader
      :speed="2"
      viewBox="0 0 205 120"
      width="205"
      height="120"
      class="absolute"
      preserve-aspect-ratio="xMinYMin slice"
      primary-color="#f3f3f3"
      secondary-color="#ecebeb"
    >
      <rect x="0" y="0" rx="3" ry="3" width="100" height="25" />
      <rect x="0" y="32.5" rx="3" ry="3" width="120" height="30" />
      <rect x="0" y="70" rx="3" ry="3" width="70" height="25" />
      <rect x="85" y="70" rx="3" ry="3" width="110" height="25" />
    </ContentLoader>
  </div>

  <!-- Error -->
  <div v-else-if="isError" class="p-6 w-full">
    <div class="flex flex-col items-center space-y-3">
      <VueFeather type="alert-circle" class="text-error" :size="48" />
      <h2 class="text-center text-error font-bold text-lg" v-text="t('commonLabel.errorData')" />
      <span v-if="error" class="text-center text-default-disabled" v-text="t(error.message)" />
    </div>
  </div>

  <!-- Content -->
  <div v-else-if="data && data.name" class="relative p-6 w-full" :class="classes">
    <!-- Refresh Loader -->
    <div v-if="isFetching" class="absolute top-0 left-0 w-full !mt-0 !mb-0 overflow-hidden">
      <div class="h-0.5 w-full bg-primary/50 animate-linear" />
    </div>

    <!-- Title & text -->
    <div :class="{ 'opacity-50 scale-95 grayscale': isFetching }" class="space-y-3 transition-all duration-100 w-full">
      <div v-if="name" class="flex items-center gap-x-2">
        <h2 class="text-sm font-bold truncate !mt-0" :title="t(name)" v-text="t(name)" />
        <span v-if="data.description" class="text-sm text-default-disabled">
          <C4Tooltip
            :title="t(data.description.title)"
            :text="data.description.content ? t(data.description.content) : undefined"
          />
        </span>
        <div v-if="props.notif">
          <div class="text-xs font-bold text-info bg-background-main rounded p-1">
            {{ props.notif }}
          </div>
        </div>
      </div>

      <!-- Value, comparison & benchmark -->
      <div v-if="type !== 'bars' && type !== 'lines' && data.value" class="flex flex-col gap-y-1">
        <div v-if="data.value" class="flex flex-row gap-4">
          <span
            class="text-primary text-2xl font-bold"
            :class="{ '!text-default-disabled': data.value?.value === null }"
            :title="formatIndicatorTitle(data.value)"
            v-text="formatIndicatorValue({ indicator: data.value, language })"
          />

          <div v-if="data.denominator" class="flex flex-row items-center gap-2">
            <span class="text-primary font-bold">/</span>
            <span
              class="text-primary font-bold"
              :class="{ '!text-default-disabled': data.denominator?.value === null }"
              :title="formatIndicatorTitle(data.denominator)"
              v-text="formatIndicatorValue({ indicator: data.denominator, language })"
            />
          </div>
        </div>
        <span
          v-if="data.benchmark || data.comparison"
          class="flex flex-row space-x-2 divide-x divide-default-very-light"
        >
          <!-- Comparison -->
          <span
            v-if="data.comparison"
            class="font-bold"
            :class="getComparisonClass(data.comparison)"
            :title="formatIndicatorTitle(data.comparison)"
            v-text="formatIndicatorValue({ indicator: data.comparison, withPlusSign: true, language })"
          />

          <!-- Benchmark -->
          <span
            v-if="data.benchmark"
            class="pl-2 flex space-x-2 items-center"
            :class="getComparisonClass(data.benchmark)"
          >
            <HomeIcon v-if="isHomeIcon" class="text-neutral-blueGray fill-neutral-blueGray w-5" />
            <CarrefourIcon v-else class="text-neutral-blueGray fill-neutral-blueGray w-5" />
            <span
              class="font-bold text-neutral-blueGray fill-neutral-blueGray"
              :title="formatIndicatorTitle(data.benchmark)"
              v-text="formatIndicatorValue({ indicator: data.benchmark, withPlusSign: true, language })"
            />
          </span>
        </span>
      </div>

      <!-- Text & Hint -->
      <span v-if="data && data.hint" class="text-sm text-default-disabled">
        {{ data?.hint ? t(data.hint) : '' }}
        <C4Tooltip v-if="data.hintTooltip" class="inline-block ml-2" :text="t(data.hintTooltip)">
          <VueFeather class="text-default-disabled cursor-pointer align-middle" type="help-circle" :size="16" />
        </C4Tooltip>
      </span>

      <!-- Chart, breakdown or default slot -->
      <slot v-if="data" v-bind="data">
        <!-- Breakdown -->
        <div v-if="type === 'breakdown' && data.breakdown">
          <div class="mt-6">
            <IndicatorWidgetBreakdown :breakdown="data.breakdown" :is-home-icon="isHomeIcon" />
          </div>
        </div>

        <!-- Pie Chart -->
        <IndicatorWidgetChartPie v-if="type === 'pie' && data.ordinates" :ordinates="data.ordinates" />

        <!-- Lines Chart -->
        <IndicatorWidgetChartLines
          v-if="type === 'lines' && data.abscissae"
          :ordinates="data.ordinates ?? []"
          :abscissae="data.abscissae"
        />

        <!-- Bars Chart -->
        <IndicatorWidgetChartBars
          v-if="type === 'bars' && data.abscissae"
          :abscissae="data.abscissae"
          :ordinates="data.ordinates ?? []"
          type="comparisons"
        />

        <!-- Bars Chart -->
        <IndicatorWidgetChartHorizontalBars v-if="type === 'horizontal-bars' && data.bars" :bars="data.bars" />
      </slot>
    </div>
  </div>
</template>
